/**
 * Kabuk düzeyindeki çerçeve metinleri (bildirim zili, genel arama, hızlı ekle, plan kapısı). Her sayfada yüklenen
 * kabuk bileşenleri yalnız bu modülü içe aktarır; kayıt ekranı metinleri (`strings.ts`) tembel yüklenen rota parçasında
 * kalır (WP 2.8 §32: ağır form/seçici kodu ilk giriş paketini büyütmez). `trf` bu grupları yeniden dışa aktarır.
 */
export const shellStrings = {
  notifications: {
    label: 'Bildirimler',
    labelCount: (n: string) => `Bildirimler: ${n} yeni`,
    title: 'Bildirimler',
    empty: 'Yeni bildirim yok.',
    loadFailed: 'Bildirimler alınamadı.',
    loading: 'Yükleniyor…',
    reminderDue: 'Hatırlatıcı zamanı geldi',
    openRecord: 'Kaydı aç',
    complete: 'Tamamla',
    snooze: 'Bir saat ertele',
    dismiss: 'Kapat',
    done: 'Bildirim güncellendi',
    failed: 'Bildirim güncellenemedi',
    more: (n: string) => `${n} bildirim daha`,
    note: 'Yalnız erişebildiğiniz kayıtların bildirimleri ve hatırlatıcıları listelenir.',
    // WP 2.9: uygulama içi bildirimler (alan bildirimleri)
    inboxTitle: 'Size gelenler',
    remindersTitle: 'Hatırlatıcılar',
    remindersEmpty: 'Zamanı gelmiş hatırlatıcı yok.',
    // M3: onay talebi (onaylayıcıya) ve not yorumu (not sahibine)
    approvalRequested: (actor: string | null) =>
      `${actor ?? 'Bir kullanıcı'} bir teklifi onayınıza gönderdi`,
    // M5: form belgesi onay talebi
    formApprovalRequested: (actor: string | null) =>
      `${actor ?? 'Bir kullanıcı'} bir form belgesini onayınıza gönderdi`,
    feedComment: (actor: string | null) => `${actor ?? 'Bir kullanıcı'} notunuza yorum yaptı`,
    // M3 herkese açık kanallar (aktör kullanıcı değil)
    leadWebForm: 'Web formundan yeni müşteri adayı (sorumlusu sizsiniz)',
    // v3.1 S9: açık aday yeni bir başvuruyla tekrar geldi (temas olarak eklendi)
    leadReapplied: 'Müşteri adayı tekrar başvurdu (sorumlusu sizsiniz)',
    /** M3 K-M3-25: sekans adımının bitiş günü geçti (adayın sorumlusuna; e-posta gönderilmez) */
    leadSequenceStepOverdue: 'Sekans adımının bitiş günü geçti',
    // M4 (K-M4-16): görev ataması
    activityAssigned: (actor: string | null) =>
      actor ? `${actor} size bir aktivite atadı` : 'Size bir aktivite atandı',
    /** M4 WP 4.3: destek talebi ataması ve SLA ihlali (sistem; e-posta gönderilmez) */
    ticketAssigned: (actor: string | null) =>
      `${actor ?? 'Bir kullanıcı'} sizi bir destek talebine görevli olarak ekledi`,
    ticketSlaBreached: 'Destek talebinin hizmet seviyesi (SLA) hedefi aşıldı',
    ticketCustomerReply: 'Destek talebinize müşteriden yanıt geldi',
    /** v3.1 C0: kayıt sorumlusu olarak atanma (toplu atamada tek özet; bağlantı ilk kayda) */
    recordAssigned: (actor: string | null, count: number | null) => {
      const many = count !== null && count > 1;
      if (actor)
        return many
          ? `${actor} sizi ${String(count)} kaydın sorumlusu yaptı (ilki aşağıda)`
          : `${actor} sizi bu kaydın sorumlusu yaptı`;
      return many
        ? `${String(count)} kaydın sorumlusu oldunuz (ilki aşağıda)`
        : 'Bu kaydın sorumlusu oldunuz';
    },
    proposalLinkResponse: (decision: 'accepted' | 'rejected' | null) =>
      decision === 'rejected'
        ? 'Müşteri teklifi web bağlantısıyla reddetti'
        : 'Müşteri teklifi web bağlantısıyla kabul etti',
    /** M5 WP 5.3: rozet kazanımı (kazanan kullanıcıya; kayda bağlı değil) */
    badgeEarned: (name: string | null) =>
      name ? `"${name}" rozetini kazandınız` : 'Yeni bir rozet kazandınız',
    openBadges: 'Rozetlerimi gör',
    /** v3.1 S10: gelen kutusu */
    conversationMessage: 'Sorumlusu olduğunuz konuşmaya müşteriden yeni ileti',
    channelHealth: 'Bir iletişim kanalı çalışmıyor',
    openConversation: 'Konuşmayı aç',
    openChannels: 'Kanalları aç',
    /** v3 C2: otomasyon "Bildirim gönder" eylemi (kural adıyla; ileti ayrı satırda) */
    automationNotice: (rule: string | null) =>
      rule ? `Otomasyon bildirimi: ${rule}` : 'Otomasyon bildirimi',
    principalAdded: (actor: string | null, field: string | null) =>
      `${actor ?? 'Bir kullanıcı'} sizi ${field ? `"${field}" alanına` : 'bir alana'} ekledi`,
    unread: 'Okunmadı',
    markRead: 'Okundu işaretle',
    markAllRead: 'Tümünü okundu işaretle',
    loadMore: 'Daha eski bildirimler',
    // TI-013: kapsam, gruplama ve işe geçiş
    scopeLabel: 'Bildirim kapsamı',
    scopeUnread: (n: number) => `Okunmamış (${String(n)})`,
    scopeAll: 'Hepsi',
    categoryLabel: 'Tür',
    categories: {
      all: 'Tüm türler',
      assign: 'Atamalar',
      comment: 'Yorumlar',
      approval: 'Onaylar',
      support: 'Destek ve SLA',
      other: 'Diğer',
    } as Record<string, string>,
    categoryCount: (label: string, n: number) => `${label} (${String(n)})`,
    groupMore: (n: number) => `${String(n)} benzer gelişme daha`,
    groupShow: (n: number) => `${String(n)} gelişmeyi göster`,
    groupHide: 'Gelişmeleri gizle',
    markGroupRead: 'Hepsini okundu işaretle',
    removeNotification: 'Bildirimi kaldır',
    emptyUnread: 'Okunmamış bildiriminiz yok.',
    emptyCategory: 'Bu türde yüklenmiş bildirim yok.',
    openPost: 'Gönderiyi aç',
    openApprovals: 'Onaya git',
    liveNote: 'Yeni bildirimler sayfayı yenilemeden gelir.',
    pollNote: 'Bildirimler kısa aralıklarla denetleniyor.',
  },

  quickAdd: {
    label: 'Hızlı ekle',
    none: 'Eklenebilecek kayıt türü yok',
    company: 'Şirket',
    contact: 'Kişi',
    product: 'Ürün',
    activity: 'Aktivite',
  },

  plan: {
    title: 'Bu özellik planınızda yok',
    body: 'Özellik mevcut planınızda etkin değil. Plan değişikliği için hesap yöneticinizle görüşün.',
    unknownTitle: 'Bu özellik henüz sunulmuyor',
    unknownBody: 'Bu özelliğin plan bilgisi henüz tanımlı değil; özellik açılmadı.',
  },
};
